import { requestService, TAsyncActionOptions } from '@trader/services';
import { tradingRoutes } from './routes';
import { IPlaceOrderBody, IPlaceOrderResponse } from './models/placeOrder';
import {
  IEditOrderParams,
  IOrderItemsBE,
  IOrderMetricsBE,
} from './models/ordersMetrics';
import {
  IClosePositionParams,
  IEditPositionParams,
  IPositionItemsBE,
} from './models/positionsMetrics';
import {
  IRequiredMarginBE,
  IRequiredMarginParams,
} from './models/requiredMargin';
import {
  IPositionParamsBE,
  IPositionsHistoryBE,
} from './models/positionsHistory';
import {
  ICostAndChargesBE,
  IGetCostAndChargesParams,
} from './models/costAndCharges';
import {
  IAccountStatementBE,
  IGetAccountStatementParams,
} from './models/accountStatement';

export class Trading {
  routes = tradingRoutes;

  async placeOrder(
    body: IPlaceOrderBody,
    options: TAsyncActionOptions
  ): Promise<IPlaceOrderResponse> {
    const resp = await requestService.post<IPlaceOrderResponse>(
      this.routes.placeOrder,
      body,
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async getOrdersMetrics(options: TAsyncActionOptions): Promise<IOrderItemsBE> {
    const resp = await requestService.get<IOrderItemsBE>(
      this.routes.getOrdersMetrics,
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async getPositionsMetrics(
    options: TAsyncActionOptions
  ): Promise<IPositionItemsBE> {
    const resp = await requestService.get<IPositionItemsBE>(
      this.routes.getPositionsMetrics,
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async getOrders(options: TAsyncActionOptions): Promise<IOrderItemsBE> {
    const resp = await requestService.get<IOrderItemsBE>(
      this.routes.getOrders,
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async getPositions(options: TAsyncActionOptions): Promise<IPositionItemsBE> {
    const resp = await requestService.get<IPositionItemsBE>(
      this.routes.getPositions,
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async getRequiredMargin(
    params: IRequiredMarginParams,
    options: TAsyncActionOptions
  ): Promise<IRequiredMarginBE> {
    const resp = await requestService.get<IRequiredMarginBE>(
      this.routes.getRequiredMargin(params),
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async cancelOrder(
    orderId: IOrderMetricsBE['orderId'],
    options: TAsyncActionOptions
  ): Promise<void> {
    const resp = await requestService.delete<void>(
      this.routes.cancelOrder(orderId),
      {},
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async editOrder(
    { orderId, body }: IEditOrderParams,
    options: TAsyncActionOptions
  ): Promise<void> {
    const resp = await requestService.put<void>(
      this.routes.editOrder(orderId),
      body,
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async closePosition(
    closePositionParams: IClosePositionParams,
    options: TAsyncActionOptions
  ): Promise<IPlaceOrderResponse> {
    const resp = await requestService.delete<IPlaceOrderResponse>(
      this.routes.closePosition(closePositionParams),
      {},
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async editPosition(
    { positionId, body }: IEditPositionParams,
    options: TAsyncActionOptions
  ): Promise<void> {
    const resp = await requestService.put<void>(
      this.routes.editPosition(positionId),
      body,
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async getPositionsHistory(
    params: IPositionParamsBE,
    options: TAsyncActionOptions
  ): Promise<IPositionsHistoryBE> {
    const resp = await requestService.get<IPositionsHistoryBE>(
      this.routes.getPositionsHistory,
      {
        ...options.cancelToken,
      }
    );
    return resp?.data;
  }

  async getCostAndCharges(
    model: IGetCostAndChargesParams,
    options?: TAsyncActionOptions
  ): Promise<ICostAndChargesBE> {
    const resp = await requestService.get<ICostAndChargesBE>(
      this.routes.getCostAndCharges(model),
      {
        ...options?.cancelToken,
      }
    );
    return resp?.data;
  }

  async getAccountStatement(
    model: IGetAccountStatementParams,
    options?: TAsyncActionOptions
  ): Promise<IAccountStatementBE> {
    const resp = await requestService.get<IAccountStatementBE>(
      this.routes.getAccountStatement(model),
      {
        ...options?.cancelToken,
      }
    );
    return resp?.data;
  }
}
